import { Drawer, DrawerBody, DrawerContent, DrawerOverlay, DrawerProps } from '@chakra-ui/modal'
import styled from '@emotion/styled'
import * as React from 'react'

import { BgColorProvider } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { buildButtonVariantProps } from '~ui/components/buttons/Button/buildButtonVariantProps'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { IconClose } from '~ui/components/icons/specific/IconX'
import { Box, FlexCentered, GridRow } from '~ui/components/layout/Box'
import { NoScrollbarBox } from '~ui/components/layout/NoScrollbarBox'
import { radiuses } from '~ui/styleGuide/radiuses'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'
import { useTheme } from '~ui/theme/ThemeProvider'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

type TChakraProps = Pick<DrawerProps, 'isOpen' | 'onClose' | 'isFullHeight' | 'blockScrollOnMount'>

type TProps = RequiredChildren &
  TChakraProps & {
    /**
     * color of the drawer
     * also used to deduce default color of the text
     */
    bgColor?: TColorKey
    /**
     * space inside the drawer,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * minimal height of the open drawer,
     * don’t go over 90vh!,
     * whatever is not used will be under the drawer content (between the content and bottom close button)
     */
    minHeight?: string
    /**
     * maximal height of the open drawer before overflow: scroll kicks in,
     * recommended value is around 90vh in most cases (also set as a default)
     */
    maxHeight?: string
    /**
     * overflow of the Chakra UI DrawerContent component
     */
    overflow?: PropsOf<typeof DrawerContent>['overflow']

    /**
     * group of props used by the header part of the drawer
     */
    headerProps?: {
      /**
       * option to render the Drawer without header (when implementing custom one)
       */
      noHeader?: boolean
      /**
       * whether the header should be part of the content flow (relative position)
       * or be over the content (absolute position, useful eg. when there is an image on the top of the drawer content)
       */
      overContent?: boolean
      /**
       * background color of the whole header
       * also might be used to decide text color (if title inherits that color)
       */
      bgColor?: PropsOf<typeof GridRow>['bgColor']
      /**
       * outer shadow of the whole header (mostly bottom shadow)
       * useful for visual separation from the drawer content
       */
      shadow?: PropsOf<typeof GridRow>['shadow']
      /**
       * drawer header padding
       */
      padding?: PropsOf<typeof GridRow>['padding']
      /**
       * text & styles of the drawer title rendered in the middle of the header
       */
      title?: React.ReactNode
      /**
       * what icon should the header close icon use (mostly cross/back arrow)
       */
      closeIcon?: PropsOf<typeof ButtonIcon>['icon']
      /**
       * background color of the button used to close the modal on the top side of it (icon),
       * can be used to highlight the icon, for example on mixed background (like an image),
       * also used to deduce the color of that icon
       * ! if closeBtnBgColor is set to something the close btn will not have its position shifted by its extended touch-zone
       * (meaning this might change its position/behavior – explore the implementation if needed)
       */
      closeBtnBgColor?: PropsOf<typeof ButtonIcon>['bgColor']
      /**
       * shadow of the header close button (for additional highlight options)
       */
      closeBtnShadow?: PropsOf<typeof ButtonIcon>['shadow']
      /**
       * at which side of the title should the component be placed
       */
      closeIconSide?: 'left' | 'right'
      /**
       * tweak position of the top close button (icon),
       * useful mainly with `overContent: true`, when the button gets positioned absolutely
       * and doesn’t care about drawer’s padding value,
       * in such case, recommended usage is in combination with `spaces` object, eg.
       * `closeBtnOffset: { top: spaces['smallMedium'], left: spaces['smallMedium'] }`
       */
      closeBtnOffset?: {
        top?: string
        left?: string
      }
    }

    /**
     * BoxProps to be passed to the Box element that wraps the drawer content,
     * use for layout etc, without need for another wrapper element
     */
    containerProps?: Omit<PropsOf<typeof Box>, 'flexGrow'>

    /**
     * group of props used by the footer part of the drawer
     */
    footerProps?: {
      /**
       * option to render the Drawer without footer (when implementing custom one)
       */
      noFooter?: boolean
      /**
       * background color of the whole header
       * also might be used to decide text color (if title inherits that color)
       */
      bgColor?: PropsOf<typeof FlexCentered>['bgColor']
      /**
       * drawer footer padding
       */
      padding?: PropsOf<typeof FlexCentered>['padding']
      /**
       * outer shadow of the whole header (mostly top shadow)
       * useful for visual separation from the drawer content
       */
      shadow?: PropsOf<typeof FlexCentered>['shadow']
      /**
       * drawer footer content (most likely some close/confirm button)
       */
      content?: React.ReactNode
    }
  }

const StyledCloseBtnIcon = styled(ButtonIcon)`
  transition: left 0.3s linear;
`

const ICON_BUTTON_PADDING: TSpace = `small`

/**
 * Drawer opening from the bottom side of the page,
 * mainly useful for presenting additional information on small devices,
 * for desktop, consider using Modal instead.
 * This is a lower-level implementation – try any of the more specific Drawers that are build on top of this before implementing your own.
 */
export const DrawerBase: React.FC<TProps> = ({
  isOpen,
  onClose,
  children,
  minHeight,
  // default `hidden` to prevent content (image) from overflowing the top rounded corners // TODO Safari?
  // but it causes a layout shift within the FullScreenDrawer (in combination with selecting radio buttons ???), so it can be set to a different value if needed
  overflow = `hidden`,
  headerProps = {},
  footerProps = {},
  padding = `none`,
  maxHeight = `80vh`,
  containerProps = {},
  bgColor = `bgColors.primary`,
  ...chakraProps
}) => {
  const theme = useTheme()
  const paddingValue = getSpacingValue(padding)

  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  const headerIconTouchCompensation = headerProps.closeBtnBgColor
    ? undefined
    : `calc(-1 * ${getSpacingValue(ICON_BUTTON_PADDING)})`

  const iconProps = React.useMemo<PropsOf<typeof StyledCloseBtnIcon>>(
    () => ({
      ...buildButtonVariantProps({
        bgColor: headerProps.closeBtnBgColor,
        variant: headerProps.closeBtnBgColor ? `solid` : `ghost`,
      }),
      onClick: onClose,
      iconSize: `1.5rem`,
      position: `relative`,
      padding: ICON_BUTTON_PADDING,
      marginTop: headerIconTouchCompensation,
      icon: headerProps.closeIcon ?? IconClose,
      marginBottom: headerIconTouchCompensation,
      shadow: headerProps.closeBtnShadow ?? `none`,
      top: headerProps.closeBtnOffset?.top ?? `0rem`,
      left: headerProps.closeBtnOffset?.left ?? `0rem`,
    }),
    [
      headerIconTouchCompensation,
      headerProps.closeBtnBgColor,
      headerProps.closeBtnOffset?.left,
      headerProps.closeBtnOffset?.top,
      headerProps.closeBtnShadow,
      headerProps.closeIcon,
      onClose,
    ],
  )

  const iconSide = headerProps.closeIconSide ?? `left`

  return (
    <BgColorProvider bgColor={bgColor}>
      {({ bgColorValue, fgColors }) => (
        <Drawer
          {...chakraProps}
          closeOnEsc
          blockScrollOnMount
          closeOnOverlayClick
          isOpen={isOpen}
          onClose={onClose}
          placement={`bottom`}
        >
          <DrawerOverlay
            zIndex={200}
            bgColor={theme.specialColors.overlayHeavy}
            height={chakraProps.isFullHeight ? `100%` : undefined} // TODO solve mobile viewport problems better
          >
            <DrawerContent
              {...themeDesignProps}
              display={`flex`}
              overflow={overflow}
              bgColor={bgColorValue}
              flexDirection={`column`}
              textColor={fgColors.normal}
              borderTopRadius={chakraProps.isFullHeight ? radiuses.none : radiuses.large}
              height={chakraProps.isFullHeight ? `100%` : undefined} // TODO solve mobile viewport problems better
            >
              <DrawerBody
                flexGrow={1}
                display={`flex`}
                overflow={`hidden`}
                minHeight={minHeight}
                padding={paddingValue}
                flexDirection={`column`}
                maxHeight={chakraProps.isFullHeight ? undefined : maxHeight}
              >
                {/* HEADER */}
                {!headerProps.noHeader && (
                  <GridRow
                    top={0}
                    zIndex={100}
                    gap={`smallMedium`}
                    alignItems={`center`}
                    shadow={headerProps.shadow}
                    padding={headerProps.padding}
                    bgColor={headerProps.bgColor}
                    gridTemplateColumns={`1fr auto 1fr`}
                    position={headerProps.overContent ? `absolute` : `relative`}
                    left={headerProps.overContent && iconSide === 'left' ? 0 : undefined}
                    right={headerProps.overContent && iconSide === 'right' ? 0 : undefined}
                  >
                    <Box margin={{ right: `auto`, left: `none`, top: `none`, bottom: `none` }}>
                      {iconSide === 'left' && (
                        <StyledCloseBtnIcon
                          {...iconProps}
                          marginLeft={headerIconTouchCompensation}
                          aria-label={'Close'}
                        />
                      )}
                    </Box>
                    <Box>{headerProps.title}</Box>
                    <Box margin={{ right: `none`, left: `auto`, top: `none`, bottom: `none` }}>
                      {iconSide === 'right' && (
                        <StyledCloseBtnIcon
                          {...iconProps}
                          marginRight={headerIconTouchCompensation}
                          aria-label={'Close'}
                        />
                      )}
                    </Box>
                  </GridRow>
                )}

                {/* CONTENT */}
                <NoScrollbarBox overflow={`auto`} {...containerProps} flexGrow={1}>
                  {children}
                </NoScrollbarBox>

                {/* FOOTER */}
                {!footerProps.noFooter && (
                  <FlexCentered
                    zIndex={1}
                    padding={footerProps.padding}
                    bgColor={footerProps.bgColor}
                    shadow={footerProps.shadow ?? `none`}
                  >
                    {footerProps.content}
                  </FlexCentered>
                )}
              </DrawerBody>
            </DrawerContent>
          </DrawerOverlay>
        </Drawer>
      )}
    </BgColorProvider>
  )
}
